import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Zoom } from 'swiper/modules';
import { ExpandOutlined, LeftOutlined } from '@ant-design/icons';
import 'swiper/css';
import 'swiper/css/zoom';
import styles from './ProductGallery.module.scss';
import img_pages_products_detail_arrow_next from '@/assets/svg/pages-products-detail-arrow-next.svg';
import img_pages_products_detail_arrow_prev from '@/assets/svg/pages-products-detail-arrow-prev.svg';

const ARROW_PREV =
  img_pages_products_detail_arrow_prev;
const ARROW_NEXT =
  img_pages_products_detail_arrow_next;

function useMobileGalleryTap() {
  const [matches, setMatches] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const onChange = () => setMatches(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return matches;
}

function PlayOverlay() {
  return (
    <span className={styles.playOverlay} aria-hidden>
      <svg
        className={styles.playSvg}
        viewBox="0 0 48 48"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle cx="24" cy="24" r="22" fill="rgba(255,255,255,0.88)" />
        <path d="M20 16L34 24L20 32V16Z" fill="rgba(22,0,4,0.75)" />
      </svg>
    </span>
  );
}

function MainSlideMedia({
  item,
  videoRef,
  videoWaiting,
  videoReady,
  isMobileTap,
  openFullscreen,
  outgoing,
  isActive,
}) {
  const innerVideoRef = useRef(null);

  useEffect(() => {
    if (item.type !== 'video') return undefined;
    if (!isActive) {
      innerVideoRef.current?.pause();
      if (videoRef) videoRef.current = null;
      return undefined;
    }
    if (videoRef && innerVideoRef.current) {
      videoRef.current = innerVideoRef.current;
    }
    return () => {
      if (videoRef && videoRef.current === innerVideoRef.current) {
        videoRef.current = null;
      }
    };
  }, [isActive, item.type, item.src, videoRef]);

  if (item.type === 'video') {
    return (
      <div className={styles.videoBox}>
        <video
          ref={innerVideoRef}
          key={outgoing ? `${item.src}-out` : item.src}
          className={styles.mainVideo}
          src={item.src}
          poster={item.poster}
          controls={!outgoing}
          muted
          playsInline
          preload="auto"
        />
        {!outgoing && videoWaiting && !videoReady && (
          <div className={styles.videoLoading} aria-live="polite">
            <span className={styles.spinner} />
            <span className={styles.srOnly}>Loading video</span>
          </div>
        )}
        {isMobileTap && !outgoing ? (
          <button
            type="button"
            className={styles.mobileFsEnter}
            onClick={e => {
              e.stopPropagation();
              openFullscreen();
            }}
            aria-label="Open fullscreen gallery"
          >
            <ExpandOutlined />
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <img
      className={styles.mainImage}
      src={item.src}
      alt={item.alt || ''}
      referrerPolicy="no-referrer"
      draggable={false}
      onClick={
        isMobileTap && !outgoing
          ? e => {
              e.stopPropagation();
              openFullscreen();
            }
          : undefined
      }
    />
  );
}

const ProductGallery = ({ items }) => {
  const count = items.length;
  const [activeIndex, setActiveIndex] = useState(0);
  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  const [fsInitialSlide, setFsInitialSlide] = useState(0);
  const [fsSessionKey, setFsSessionKey] = useState(0);
  const [videoReady, setVideoReady] = useState(false);
  const [videoWaiting, setVideoWaiting] = useState(false);
  const videoRef = useRef(null);
  const thumbRefs = useRef([]);
  const mainSwiperRef = useRef(null);
  const isMobileTap = useMobileGalleryTap();

  const current = items[activeIndex];
  const isVideo = current?.type === 'video';
  const manyThumbs = count > 4;

  const itemsKey = items.map(i => `${i.type}-${i.src}`).join('|');

  useEffect(() => {
    setActiveIndex(0);
    mainSwiperRef.current?.slideTo(0, 0);
  }, [itemsKey]);

  const onThumbClick = useCallback(
    index => {
      if (count <= 1 || index === activeIndex) return;
      mainSwiperRef.current?.slideTo(index);
    },
    [count, activeIndex]
  );

  useEffect(() => {
    const el = thumbRefs.current[activeIndex];
    if (el && manyThumbs) {
      el.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  }, [activeIndex, manyThumbs]);

  useEffect(() => {
    if (!isVideo) {
      setVideoReady(false);
      setVideoWaiting(false);
      if (videoRef.current) {
        videoRef.current.pause();
      }
    }
  }, [isVideo]);

  useEffect(() => {
    const v = videoRef.current;
    if (!isVideo || !v) return undefined;

    setVideoReady(false);
    setVideoWaiting(true);

    const onReady = () => {
      setVideoReady(true);
      setVideoWaiting(false);
    };
    const onWait = () => setVideoWaiting(true);
    const onPlay = () => setVideoWaiting(false);

    v.addEventListener('canplay', onReady);
    v.addEventListener('loadeddata', onReady);
    v.addEventListener('waiting', onWait);
    v.addEventListener('playing', onPlay);

    if (v.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
      onReady();
    }

    return () => {
      v.removeEventListener('canplay', onReady);
      v.removeEventListener('loadeddata', onReady);
      v.removeEventListener('waiting', onWait);
      v.removeEventListener('playing', onPlay);
    };
  }, [isVideo, activeIndex, current?.src]);

  useEffect(() => {
    const v = videoRef.current;
    if (!isVideo || !v || !videoReady) return undefined;
    const playPromise = v.play();
    if (playPromise !== undefined) {
      playPromise.catch(() => {});
    }
    return () => {
      v.pause();
    };
  }, [isVideo, videoReady, activeIndex, current?.src]);

  useEffect(() => {
    if (fullscreenOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
    return undefined;
  }, [fullscreenOpen]);

  const openFullscreen = useCallback(() => {
    setFsSessionKey(k => k + 1);
    setFsInitialSlide(activeIndex);
    setFullscreenOpen(true);
  }, [activeIndex]);

  const closeFullscreen = () => setFullscreenOpen(false);

  return (
    <div className={styles.root}>
      <div className={styles.mainWrap}>
        <div
          className={classNames(
            styles.mainStage,
            isMobileTap && styles.mainStageTap,
            isVideo && styles.mainStageVideo
          )}
        >
          <div className={styles.mainViewport} aria-live="polite">
            <Swiper
              className={styles.mainSwiper}
              slidesPerView={1}
              spaceBetween={0}
              rewind={count > 1}
              speed={400}
              touchRatio={1}
              resistanceRatio={0.85}
              onSwiper={swiper => {
                mainSwiperRef.current = swiper;
              }}
              onSlideChange={swiper => setActiveIndex(swiper.realIndex)}
            >
              {items.map((item, index) => (
                <SwiperSlide key={`${item.type}-${item.src}-${index}`} className={styles.mainSlide}>
                  <MainSlideMedia
                    item={item}
                    videoRef={videoRef}
                    videoWaiting={videoWaiting}
                    videoReady={videoReady}
                    isMobileTap={isMobileTap}
                    openFullscreen={openFullscreen}
                    outgoing={false}
                    isActive={activeIndex === index}
                  />
                </SwiperSlide>
              ))}
            </Swiper>
          </div>

          <button
            type="button"
            className={classNames(styles.navBtn, styles.navPrev)}
            onClick={e => {
              e.stopPropagation();
              mainSwiperRef.current?.slidePrev();
            }}
            aria-label="Previous image"
          >
            <img src={ARROW_PREV} alt="" referrerPolicy="no-referrer" />
          </button>
          <button
            type="button"
            className={classNames(styles.navBtn, styles.navNext)}
            onClick={e => {
              e.stopPropagation();
              mainSwiperRef.current?.slideNext();
            }}
            aria-label="Next image"
          >
            <img src={ARROW_NEXT} alt="" referrerPolicy="no-referrer" />
          </button>
        </div>
      </div>

      <div
        className={classNames(
          styles.thumbs,
          manyThumbs ? styles.thumbsMany : styles.thumbsFew
        )}
        role="tablist"
        aria-label="Product gallery thumbnails"
      >
        {items.map((item, index) => {
          const active = index === activeIndex;
          return (
            <button
              key={`${item.type}-${item.src}-${index}`}
              ref={el => {
                thumbRefs.current[index] = el;
              }}
              type="button"
              role="tab"
              aria-selected={active}
              className={classNames(styles.thumb, active && styles.thumbActive)}
              onClick={() => onThumbClick(index)}
              aria-label={
                item.type === 'video'
                  ? `Play video, slide ${index + 1} of ${count}`
                  : `View image ${index + 1} of ${count}`
              }
            >
              <span className={styles.thumbInner}>
                <img
                  src={item.type === 'video' ? item.poster || item.src : item.src}
                  alt=""
                  referrerPolicy="no-referrer"
                />
                {item.type === 'video' ? <PlayOverlay /> : null}
              </span>
            </button>
          );
        })}
      </div>

      {fullscreenOpen &&
        createPortal(
          <div className={styles.fsRoot} role="dialog" aria-modal="true" aria-label="Gallery">
            <div className={styles.fsChrome}>
              <button
                type="button"
                className={styles.fsBack}
                onClick={closeFullscreen}
                aria-label="Close gallery"
              >
                <LeftOutlined />
              </button>
              <div className={styles.fsCounter} aria-live="polite">
                {activeIndex + 1}/{count}
              </div>
            </div>
            <div className={styles.fsViewport}>
              <Swiper
                key={fsSessionKey}
                className={styles.fsSwiper}
                modules={[Zoom]}
                zoom={{ maxRatio: 4, minRatio: 1 }}
                rewind={count > 1}
                spaceBetween={0}
                slidesPerView={1}
                initialSlide={fsInitialSlide}
                onSlideChange={swiper => setActiveIndex(swiper.activeIndex)}
                touchRatio={1}
                resistanceRatio={0.85}
              >
              {items.map((item, index) =>
                item.type === 'video' ? (
                  <SwiperSlide key={`fs-v-${index}`} className={styles.fsSlide}>
                    <div className={styles.fsVideoWrap}>
                      <video
                        className={styles.fsVideo}
                        src={item.src}
                        poster={item.poster}
                        controls
                        playsInline
                        preload="metadata"
                      />
                    </div>
                  </SwiperSlide>
                ) : (
                  <SwiperSlide key={`fs-i-${index}`} className={styles.fsSlide}>
                    <div className="swiper-zoom-container">
                      <img
                        className={styles.fsImage}
                        src={item.src}
                        alt={item.alt || ''}
                        referrerPolicy="no-referrer"
                        draggable={false}
                      />
                    </div>
                  </SwiperSlide>
                )
              )}
              </Swiper>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default ProductGallery;
